/**
 * CAOS-145 — the Setup app's ACCESS EXPLAIN surface: why one person can, or cannot, reach one
 * record, and which clause decided it.
 *
 * ── WHY THIS IS THE WHOLE TICKET ────────────────────────────────────────────────────────────
 * The engine behind this screen already exists and is correct. `security/record-access-explain.ts`
 * resolves the clause-by-clause record-plane explanation, `security/field-access-explain.ts` the
 * field plane, and `api/access-routes.ts` (CAOS-118, CAOS-962, CAOS-1508) exposes both as the
 * `access` noun's two verbs. What did not exist was anything that DREW them: before this file,
 * nothing under `host/src` imported either resolver or called either verb, so the answer was
 * reachable only by an administrator willing to hand-craft an API call. A capability that is built,
 * gated and unread is the CAOS-1492 shape, and the remaining work is the view.
 *
 * ── THE ONE RULE THIS SURFACE OBEYS ─────────────────────────────────────────────────────────
 * `dev-docs`'s setup-metadata-model.md states it for this screen by name: an access-explain view
 * "reads from the describe layer and the effective-access resolver rather than reconstructing
 * access by hand". Nothing in this file decides anything about access. It collects a question,
 * sends it, and paints the answer. That is not fastidiousness — a second reading of the access
 * union would drift from the first, and neither side would look wrong while it did.
 *
 * It is also why BOTH verbs are called for one question rather than one of them. `explain` returns
 * every clause but does not nominate a headline reason; `check` does, and its choice is a real
 * decision the kernel documents — CAOS-1508 looks for a refused OBJECT plane FIRST, because when
 * the object grant is what denies, the record-plane clauses below it still carry `would_require`
 * sentences, and citing one of those sends an administrator to fix ownership or a sharing rule
 * when neither change would make the read work. Deriving the headline here would be re-deriving
 * exactly the judgement that paragraph exists to get right. So the sentence at the top of the
 * result comes from `check`, and the clause list beneath it from `explain`.
 *
 * ── THE SHAPE, WHICH MATCHES THE REST OF SETUP ──────────────────────────────────────────────
 * A thin route-shape bridge over kernel verbs, the same way `setup-domains.ts` and `seam-order.ts`
 * are: no metadata of its own, no kernel change, every fetch fail-soft so a refusal paints a real
 * message rather than a crash or a blank pane. A package-contributed Setup page (CAOS-582) was the
 * other candidate and is the wrong mechanism rather than merely a different one: what a
 * contribution draws is a `rosterPage` (plus an optional record page) through the generic
 * renderer — a list and the rows behind it. This screen asks a question, and a roster cannot.
 *
 * ── WHY THE SUBJECT PICKER IS ALLOWED TO BE ABSENT ──────────────────────────────────────────
 * Both `access` verbs are gated `security.manage`. `principals list` — the only verb that
 * enumerates the org's people — is gated `users.manage`. Those are different permissions and
 * neither implies the other, so an administrator holding exactly what this screen requires may not
 * be allowed to list anybody. The picker is therefore fail-soft in the strong sense: when the list
 * answers, the subject is chosen from a menu; when it refuses, a principal id is typed instead and
 * the screen is fully usable. Both forms default to the signed-in administrator, which is what
 * both verbs already do when no subject is given. A screen that hard-required the people list
 * would be unusable by precisely the person it is for.
 *
 * ── AND WHY THE RECORD IS AN ID ─────────────────────────────────────────────────────────────
 * The record-plane question is about one row; the verbs take a `recordId`; and the platform has no
 * cross-object record search for a surface like this to borrow. An administrator asking this
 * question is looking at the record that prompted it. That is a property of the question, not a
 * gap in this screen.
 */

export type Navigate = (href: string) => void;

export interface MetaResponse {
  json(): Promise<unknown>;
}
export type MetaFetch = (path: string, init?: RequestInit) => Promise<MetaResponse>;

const defaultMetaFetch: MetaFetch = (path, init) =>
  fetch(path, { credentials: 'same-origin', ...init }) as unknown as Promise<MetaResponse>;

let metaFetch: MetaFetch = defaultMetaFetch;

/** Override the API seam (production wiring / deterministic tests). */
export function setSetupAccessExplainFetch(fn: MetaFetch): void {
  metaFetch = fn;
}

interface Envelope {
  ok?: boolean;
  data?: unknown;
  error?: { message?: unknown; code?: unknown };
}

/** The record-access plane, as both verbs name it. */
type AccessPlane = 'read' | 'write';

// ── the wire, mirrored read-only ────────────────────────────────────────────────────────────
// These interfaces describe what the two verbs ANSWER. Every member is optional and every reader
// below tolerates its absence: this file is downstream of a kernel it does not ship with, and a
// surface that threw on an unexpected payload would turn a new field into a blank pane.

interface ClauseView {
  kind?: string;
  status?: string;
  detail?: string;
}

interface OwnerView {
  kind?: string;
  id?: string | null;
  label?: string;
}

interface FieldView {
  name?: string;
  access?: string;
  readable?: boolean;
  editable?: boolean;
  reason?: string;
}

interface ExplanationView {
  plane?: string;
  objectKey?: string;
  recordId?: string;
  subjectPrincipalId?: string;
  recordExists?: boolean;
  owner?: OwnerView;
  admitted?: boolean;
  admittedBy?: ClauseView | null;
  objectPlaneAdmits?: boolean;
  recordPlaneAdmitted?: boolean;
  clauses?: ClauseView[];
  planeNote?: string;
}

interface CheckView {
  allowed?: boolean;
  reason?: string;
  recordExists?: boolean;
  admittedBy?: string | null;
  objectPlaneAdmits?: boolean;
  recordPlaneAdmitted?: boolean;
  planeNote?: string;
  field?: FieldView;
}

interface ObjectRef {
  key: string;
  label: string;
}

interface PersonRef {
  id: string;
  label: string;
}

/** POST one kernel verb. Never throws — the caller paints whatever comes back. */
async function post(path: string, body: unknown): Promise<{ ok: true; data: unknown } | { ok: false; message: string }> {
  let env: Envelope;
  try {
    const res = await metaFetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    env = (await res.json()) as Envelope;
  } catch {
    return { ok: false, message: 'The server could not be reached. Please try again.' };
  }
  if (env?.ok !== true) {
    const message = typeof env?.error?.message === 'string' ? env.error.message : 'The request was refused.';
    return { ok: false, message };
  }
  return { ok: true, data: env.data };
}

function humanize(key: string): string {
  return key
    .replace(/[-_.]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** Every component of one type, or null when the read refused — the same seam Domains reads. */
async function retrieve(type: string): Promise<{ key: string; body?: Record<string, unknown> }[] | null> {
  let env: Envelope;
  try {
    const res = await metaFetch(`/api/metadata/retrieve?type=${encodeURIComponent(type)}`, { method: 'GET' });
    env = (await res.json()) as Envelope;
  } catch {
    return null;
  }
  if (env?.ok !== true) return null;
  const components = (env.data as { components?: unknown } | null)?.components;
  if (!Array.isArray(components)) return null;
  return components.filter((c): c is { key: string; body?: Record<string, unknown> } => typeof (c as { key?: unknown })?.key === 'string');
}

/** The org's objects, by component key — what the `object` param names. */
async function fetchObjects(): Promise<ObjectRef[] | null> {
  const components = await retrieve('object');
  if (!components) return null;
  return components
    .map((c) => {
      const label = c.body?.['label'];
      return { key: c.key, label: typeof label === 'string' && label.trim() ? label : humanize(c.key) };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The BARE field keys on one object.
 *
 * Field components are keyed object-qualified (`<object>.<field>`); the verb's `field` param is the
 * bare key and validates it against the object's own fields, so the prefix is stripped here rather
 * than sent and rejected.
 */
async function fetchFields(objectKey: string): Promise<ObjectRef[] | null> {
  const components = await retrieve('field');
  if (!components) return null;
  const prefix = `${objectKey}.`;
  return components
    .filter((c) => c.key.startsWith(prefix))
    .map((c) => {
      const bare = c.key.slice(prefix.length);
      const label = c.body?.['label'];
      return { key: bare, label: typeof label === 'string' && label.trim() ? label : humanize(bare) };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The org's people, or null when this administrator may not enumerate them.
 *
 * `null` is an ORDINARY outcome here, not an error to report: see the module header. It means the
 * subject is typed rather than picked, and nothing else about the screen changes.
 */
async function fetchPeople(): Promise<PersonRef[] | null> {
  const result = await post('/api/principals/list', {});
  if (!result.ok) return null;
  const principals = (result.data as { principals?: unknown } | null)?.principals;
  if (!Array.isArray(principals)) return null;
  return principals
    .filter((p): p is { id: string; displayName?: string | null; identifier?: string | null; status?: string } =>
      typeof (p as { id?: unknown })?.id === 'string',
    )
    .map((p) => {
      const name = typeof p.displayName === 'string' && p.displayName.trim() ? p.displayName : null;
      const identifier = typeof p.identifier === 'string' && p.identifier.trim() ? p.identifier : null;
      const named = name && identifier ? `${name} (${identifier})` : (name ?? identifier ?? p.id);
      return { id: p.id, label: p.status && p.status !== 'active' ? `${named} — ${p.status}` : named };
    });
}

// ── DOM helpers, mirroring setup-domains.ts ─────────────────────────────────────────────────

function button(label: string, opts: { variant?: string } = {}): HTMLElement {
  const b = document.createElement('caos-button');
  if (opts.variant) b.setAttribute('variant', opts.variant);
  b.setAttribute('size', 'sm');
  b.setAttribute('type', 'button');
  b.textContent = label;
  return b;
}

function banner(kind: 'error' | 'status' | 'notice', message: string): HTMLElement {
  const el = document.createElement('div');
  el.className = `setup-access-explain-${kind}`;
  el.setAttribute('role', kind === 'error' ? 'alert' : 'status');
  el.textContent = message;
  return el;
}

function header(): HTMLElement {
  const head = document.createElement('caos-record-header');
  head.setAttribute('object-label', 'Setup · Platform');
  head.setAttribute('heading', 'Access Explain');
  head.setAttribute('no-icon', '');
  return head;
}

/**
 * One labelled control.
 *
 * The label wraps the control rather than pointing at it by `for`/`id`: a field key can contain a
 * dot, and an id selector built from one parses as an id plus a class. Wrapping associates them
 * without minting an id at all.
 */
function labelled(text: string, control: HTMLElement, hint?: string): HTMLElement {
  const label = document.createElement('label');
  label.className = 'setup-access-explain-field';
  const caption = document.createElement('span');
  caption.className = 'setup-access-explain-label';
  caption.textContent = text;
  label.append(caption, control);
  if (hint) {
    const note = document.createElement('span');
    note.className = 'setup-access-explain-hint';
    note.textContent = hint;
    label.append(note);
  }
  return label;
}

function select(options: readonly { value: string; label: string }[], name: string): HTMLSelectElement {
  const el = document.createElement('select');
  el.className = 'setup-access-explain-select';
  el.setAttribute('data-field', name);
  for (const option of options) {
    const node = document.createElement('option');
    node.value = option.value;
    node.textContent = option.label;
    el.append(node);
  }
  return el;
}

function textInput(name: string, placeholder: string): HTMLInputElement {
  const el = document.createElement('input');
  el.type = 'text';
  el.className = 'setup-access-explain-input';
  el.setAttribute('data-field', name);
  el.placeholder = placeholder;
  el.setAttribute('aria-label', humanize(name));
  return el;
}

/** A yes/no mark for one plane. The word is the content; the class only colours it. */
function verdictChip(label: string, admits: boolean | undefined): HTMLElement {
  const chip = document.createElement('span');
  chip.className = `setup-access-explain-chip ${admits === true ? 'is-allowed' : admits === false ? 'is-denied' : 'is-unknown'}`;
  chip.textContent = admits === true ? `${label}: admits` : admits === false ? `${label}: refuses` : `${label}: not reported`;
  return chip;
}

/**
 * The clause list — every clause in the order the engine evaluated it, status and sentence intact.
 *
 * Rendered whole, including the clauses that did not apply, because "what would have let them in"
 * is the half of the answer an administrator acts on. A list filtered down to the deciding clause
 * would answer "no" without ever saying what would change it.
 */
function clauseList(clauses: readonly ClauseView[]): HTMLElement {
  const list = document.createElement('ol');
  list.className = 'setup-access-explain-clauses';
  for (const clause of clauses) {
    const item = document.createElement('li');
    item.className = `setup-access-explain-clause is-${clause.status ?? 'unknown'}`;

    const head = document.createElement('p');
    head.className = 'setup-access-explain-clause-head';
    const kind = document.createElement('span');
    kind.className = 'setup-access-explain-clause-kind';
    kind.textContent = humanize(clause.kind ?? 'unknown');
    const status = document.createElement('span');
    status.className = 'setup-access-explain-clause-status';
    status.textContent = humanize(clause.status ?? 'unknown');
    head.append(kind, status);

    const detail = document.createElement('p');
    detail.className = 'setup-access-explain-clause-detail';
    detail.textContent = clause.detail ?? '';

    item.append(head, detail);
    list.append(item);
  }
  return list;
}

/** The field plane, when a field was asked about — independent of the record plane, and said so. */
function fieldCard(field: FieldView): HTMLElement {
  const card = document.createElement('section');
  card.className = 'setup-access-explain-field-plane';

  const heading = document.createElement('h3');
  heading.className = 'setup-access-explain-subheading';
  heading.textContent = `Field: ${field.name ?? ''}`;

  const tier = document.createElement('p');
  tier.className = `setup-access-explain-tier is-${field.access ?? 'unknown'}`;
  tier.textContent =
    field.access === 'edit'
      ? 'Editable'
      : field.access === 'read'
        ? 'Read-only'
        : field.access === 'none'
          ? 'Not visible'
          : 'Not reported';

  const reason = document.createElement('p');
  reason.className = 'setup-access-explain-tier-reason';
  reason.textContent = field.reason ?? '';

  const note = document.createElement('p');
  note.className = 'setup-access-explain-note';
  note.textContent =
    'The field plane is resolved independently of the record plane: reaching the record says nothing about whether this column is visible, and vice versa.';

  card.append(heading, tier, reason, note);
  return card;
}

/** The verdict, built from BOTH verbs — the headline from `check`, the clauses from `explain`. */
function resultPane(check: CheckView, explanation: ExplanationView | null): HTMLElement {
  const pane = document.createElement('section');
  pane.className = 'setup-access-explain-result';

  const verdict = document.createElement('p');
  verdict.className = `setup-access-explain-verdict ${check.allowed === true ? 'is-allowed' : 'is-denied'}`;
  verdict.setAttribute('role', 'status');
  verdict.textContent = check.allowed === true ? 'Allowed' : 'Not allowed';
  pane.append(verdict);

  const reason = document.createElement('p');
  reason.className = 'setup-access-explain-reason';
  reason.textContent = check.reason ?? '';
  pane.append(reason);

  // CAOS-1508 — the two planes are reported SEPARATELY and always. A verdict that showed only the
  // combined answer could not tell an administrator which plane refused, and the fix for an
  // object-grant refusal has nothing in common with the fix for a record-plane one.
  //
  // The two verbs are two SEPARATE requests, so they can in principle observe different state — a
  // permission revoked between them, say. Preferring one silently is the failure this whole screen
  // exists to avoid, so where they both answer and disagree, the disagreement is SAID rather than
  // resolved. `check` is still what is drawn — not because that avoids a contradiction, since a
  // notice saying the two reads disagree IS the contradiction, deliberately placed where a reader
  // can see it. It is drawn because the headline sentence above came from `check`, so any other
  // choice would put the chip at odds with the sentence beside it AND leave the disagreement
  // unsaid. This way exactly one thing on screen is unresolved, and it is the thing that names
  // itself as unresolved.
  const disagreed: string[] = [];
  const plane = (label: string, fromCheck: boolean | undefined, fromExplain: boolean | undefined): boolean | undefined => {
    if (fromCheck !== undefined && fromExplain !== undefined && fromCheck !== fromExplain) disagreed.push(label);
    return fromCheck ?? fromExplain;
  };

  const planes = document.createElement('div');
  planes.className = 'setup-access-explain-planes';
  planes.append(
    verdictChip('Object plane', plane('object plane', check.objectPlaneAdmits, explanation?.objectPlaneAdmits)),
    verdictChip('Record plane', plane('record plane', check.recordPlaneAdmitted, explanation?.recordPlaneAdmitted)),
  );
  pane.append(planes);

  if (disagreed.length > 0) {
    pane.append(
      banner(
        'notice',
        `The two reads disagreed about the ${disagreed.join(' and the ')}. They are separate requests, so access may have changed between them — ask again to see the settled answer.`,
      ),
    );
  }

  if (check.recordExists === false || explanation?.recordExists === false) {
    pane.append(
      banner('notice', 'This record does not exist in this org, or is not visible at all — so there is no owner and no record-plane clause to explain.'),
    );
  }

  if (explanation) {
    const facts = document.createElement('dl');
    facts.className = 'setup-access-explain-facts';
    for (const [term, value] of [
      ['Owner', explanation.owner?.label ?? ''],
      ['Admitted by', explanation.admittedBy?.kind ? humanize(explanation.admittedBy.kind) : 'Nothing'],
    ] as const) {
      const dt = document.createElement('dt');
      dt.textContent = term;
      const dd = document.createElement('dd');
      dd.textContent = value;
      facts.append(dt, dd);
    }
    pane.append(facts);

    const clauses = explanation.clauses ?? [];
    const heading = document.createElement('h3');
    heading.className = 'setup-access-explain-subheading';
    heading.textContent = 'Every clause, in the order the engine evaluated it';
    pane.append(heading);
    pane.append(clauses.length > 0 ? clauseList(clauses) : banner('notice', 'The engine reported no clauses for this record.'));

    if (explanation.planeNote) {
      const note = document.createElement('p');
      note.className = 'setup-access-explain-note';
      note.textContent = explanation.planeNote;
      pane.append(note);
    }
  }

  if (check.field) pane.append(fieldCard(check.field));

  return pane;
}

export interface SetupAccessExplainContext {
  readonly navigate?: Navigate;
}

/**
 * Draw the surface. `done` resolves once the pickers have been populated (or have failed softly),
 * which is what a test waits on before filling the form in.
 */
export function renderSetupAccessExplain(_ctx: SetupAccessExplainContext = {}): {
  root: HTMLElement;
  done: Promise<void>;
} {
  const root = document.createElement('div');
  root.className = 'setup-access-explain';
  root.append(header());

  const intro = document.createElement('p');
  intro.className = 'setup-access-explain-intro';
  intro.textContent =
    'Ask why one person can or cannot reach one record, and see the clause that decided it. Every answer comes from the same resolver the platform itself reads, so it cannot disagree with what a real read admits.';
  root.append(intro);

  const form = document.createElement('div');
  form.className = 'setup-access-explain-form';
  root.append(form);

  const banners = document.createElement('div');
  banners.className = 'setup-access-explain-banners';
  root.append(banners);

  const results = document.createElement('div');
  results.className = 'setup-access-explain-results';
  root.append(results);

  // The object control is decided by the load below; until then it is a plain text box, so the
  // screen is answerable even if the metadata read never returns.
  let objectControl: HTMLSelectElement | HTMLInputElement = textInput('object', 'object_api_name');
  let subjectControl: HTMLSelectElement | HTMLInputElement = textInput('subject', 'Leave blank for yourself');
  let fieldControl: HTMLSelectElement | HTMLInputElement = textInput('field', 'Optional — a field on this object');

  const recordControl = textInput('recordId', '00000000-0000-0000-0000-000000000000');
  const planeControl = select(
    [
      { value: 'read', label: 'Read' },
      { value: 'write', label: 'Write' },
    ],
    'plane',
  );

  const objectSlot = document.createElement('div');
  const subjectSlot = document.createElement('div');
  const fieldSlot = document.createElement('div');

  const ask = button('Explain access', { variant: 'primary' });

  const paintForm = (): void => {
    objectSlot.replaceChildren(labelled('Object', objectControl));
    subjectSlot.replaceChildren(
      labelled('Person', subjectControl, 'Leave this as yourself to ask about your own access.'),
    );
    fieldSlot.replaceChildren(
      labelled('Field', fieldControl, 'Optional. The field plane is answered separately from the record plane.'),
    );
  };

  paintForm();
  form.append(
    objectSlot,
    labelled('Record ID', recordControl, 'The id of the one record this question is about.'),
    subjectSlot,
    labelled('Plane', planeControl),
    fieldSlot,
    ask,
  );

  const valueOf = (control: HTMLSelectElement | HTMLInputElement): string => control.value.trim();

  /** Repopulate the field control for whichever object is currently chosen. */
  const loadFields = async (): Promise<void> => {
    const objectKey = valueOf(objectControl);
    if (!objectKey) return;
    const fields = await fetchFields(objectKey);
    if (!fields || fields.length === 0) return;
    fieldControl = select(
      [{ value: '', label: 'Record access only' }, ...fields.map((f) => ({ value: f.key, label: f.label }))],
      'field',
    );
    paintForm();
  };

  const submit = async (): Promise<void> => {
    banners.replaceChildren();
    const object = valueOf(objectControl);
    const recordId = valueOf(recordControl);
    if (!object || !recordId) {
      // The previous answer goes with it. It was true of the PREVIOUS question, and leaving it
      // under an error about this one puts a confident verdict next to a form that no longer
      // describes it — the screen would be answering a question nobody can still see.
      results.replaceChildren();
      banners.replaceChildren(banner('error', 'Name the object and the record id you want explained.'));
      return;
    }
    const subject = valueOf(subjectControl);
    const field = valueOf(fieldControl);
    const plane = (planeControl.value === 'write' ? 'write' : 'read') satisfies AccessPlane;

    const query = {
      object,
      recordId,
      ...(subject ? { subject } : {}),
      ...(field ? { field } : {}),
    };

    // Both verbs, asked about the SAME plane. `check` takes an operation rather than a plane, and
    // read/write are the two operations that map onto them (`planeForOperation`), so the pair
    // cannot answer about different planes.
    const [checked, explained] = await Promise.all([
      post('/api/access/check', { ...query, operation: plane }),
      post('/api/access/explain', { ...query, plane }),
    ]);

    if (!checked.ok) {
      results.replaceChildren();
      banners.replaceChildren(banner('error', checked.message));
      return;
    }
    if (!explained.ok) {
      // The headline survives on its own; only the clause list is lost, and saying which half is
      // missing is better than painting a verdict that looks complete.
      banners.replaceChildren(banner('notice', `The verdict below is complete, but the clause list could not be read: ${explained.message}`));
    }

    const check = checked.data as CheckView;
    const explanation = explained.ok ? ((explained.data as { explanation?: ExplanationView } | null)?.explanation ?? null) : null;
    results.replaceChildren(resultPane(check, explanation));
  };

  ask.addEventListener('click', () => {
    void submit();
  });

  const done = (async (): Promise<void> => {
    const [objects, people] = await Promise.all([fetchObjects(), fetchPeople()]);

    if (objects && objects.length > 0) {
      const chooser = select(
        objects.map((o) => ({ value: o.key, label: o.label })),
        'object',
      );
      chooser.addEventListener('change', () => {
        void loadFields();
      });
      objectControl = chooser;
    }

    if (people && people.length > 0) {
      subjectControl = select(
        [{ value: '', label: 'Yourself (the signed-in administrator)' }, ...people.map((p) => ({ value: p.id, label: p.label }))],
        'subject',
      );
    }

    paintForm();
    await loadFields();
  })();

  return { root, done };
}
